import React, { useEffect, useState } from 'react';
import AloSidebar from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloSidebar';
import { StoreLocatorTile } from './StoreLocatorTile';
import { isTagPresent, pluralize } from 'src/scripts/utils/helpers';
import { observer } from 'mobx-react-lite';
import { closeStoreLocatorSidebar, storeLocatorSidebarState } from './storeLocatorSidebarState';
import { StoreLocatorForm } from './StoreLocatorForm';
import { IStoreAvailabilityData, StoreLocatorTab } from './storeLocatorTypes';
import { ShippingCountryState } from '../../../../utils/shipping';
import { customerLocationState } from './customerLocationState';
import { LoadingMoreSpinner } from 'src/scripts/templates/product-listing-page/components/SmallUIComponents';
import { findInStoreState } from '../FindInStore/findInStoreState';
import { bopisState } from '../Bopis/bopisState';
import { SiteSettings } from 'src/scripts/utils/environment';
import './style.scss';
import SddContainer from '../Sdd/SddContainer';
import { sddState } from '../Sdd/sddState';
import {
  extractStoresDataFromBopisProductAvailabilityData,
  transformCartDataToBopisAnalyticsVariant,
  transformPdpDataToBopisAnalyticsVariant,
} from '../Bopis/bopisUtils';
import { useCartData } from 'src/scripts/utils/cart';
import { useCountryCode } from 'src/scripts/templates/_shared/react-hooks';
import { BopisAnalyticsData } from '../Bopis/bopisTypes';
import { fireBOPISDataLayerEvent } from 'src/scripts/utils/gtm-data-layer';
import { Tab, Tabs } from '@material-ui/core';
import { reaction } from 'mobx';
import { LUXURY_HANDBAG_TAG } from 'src/scripts/utils/constants';

let StoreLocatorSidebar = () => {
  const [activeTab, setActiveTab] = useState<StoreLocatorTab>('shipping');
  const postalCodeToDisplay = customerLocationState.zip !== null ? customerLocationState.zip : 'you';

  const {
    product,
    userSelectedSize,
    storeSelectionEnabled,
    storeLocatorType = 'fis',
    selectedVariant,
    isAvailableOnline,
    page,
  } = storeLocatorSidebarState.data || {};

  const cartData = useCartData();
  const countryCode = useCountryCode();

  useEffect(() => {
    // Reset active tab when sidebar opens
    const disposer = reaction(
      () => storeLocatorSidebarState.openTab,
      (newTab) => {
        setActiveTab(newTab || 'shipping');
      }
    );
    return () => disposer();
  }, []);

  const bopisAnalyticsVariant =
    selectedVariant && product ? transformPdpDataToBopisAnalyticsVariant(selectedVariant, product) : undefined;

  const bopisAnalyticsVariants =
    page === 'pdp'
      ? bopisAnalyticsVariant
        ? [bopisAnalyticsVariant]
        : []
      : transformCartDataToBopisAnalyticsVariant(cartData);

  const bopisEventDetailsTemplate = {
    countryCode,
    page: page?.toUpperCase() as BopisAnalyticsData['page'],
    isSidebar: true,
    variants: bopisAnalyticsVariants,
  };

  const storesData =
    storeLocatorType === 'fis'
      ? findInStoreState.storesData
      : page === 'pdp'
      ? extractStoresDataFromBopisProductAvailabilityData(bopisState.pdpStoresData)
      : bopisState.cartStoresData;
  const hasError = isError(storeLocatorType, page);

  const shippingInfo = page === 'pdp' ? sddState.pdpSddData : sddState.cartSddData;

  const errorMessage = SiteSettings?.bopisErrorMessage;
  const pricingDisclaimer = SiteSettings?.findInStorePricingDisclaimer;
  const noResultsCopy =
    storeLocatorType === 'fis'
      ? `Sorry, there are no ALO stores found near you. Try a different ${
          ShippingCountryState.countryCode.toLowerCase() === 'us' ? 'zip' : 'postal'
        } code.`
      : 'No stores found near you. Try a different zip code or have it shipped.';

  const shouldShowStoreLocatorForm =
    page === 'cart' && bopisState.cartStoresDataError?.detail.toLowerCase().includes('location is unknown');

  const shouldShowTileMessage = page !== 'pdp' || Boolean(customerLocationState.coordinates);
  const shouldShowStoresData = storesData && storesData.length > 0 && storeLocatorType !== 'sdd';
  const hasLuxuryTag = isTagPresent(product?.tags ?? [], LUXURY_HANDBAG_TAG);

  return (
    <AloSidebar
      title={storeLocatorSidebarState.title}
      headerBorder={true}
      visible={storeLocatorSidebarState.isOpen}
      onClose={() => {
        closeStoreLocatorSidebar();
        // Reset active tab when sidebar closes
        setActiveTab('shipping');
        storeLocatorSidebarState.openTab = 'shipping';
      }}
      onOpen={() => {
        fireBOPISDataLayerEvent({
          eventName: 'bopis_sidebar_open_event',
          countryCode,
          page: page === 'cart' ? 'Cart' : 'PDP',
          isSidebar: false,
          variants: bopisAnalyticsVariants,
        });
      }}
      desktopWidth={768}
    >
      {storeLocatorSidebarState.isLoading ? (
        <div className="store-locator-sidebar">
          <LoadingMoreSpinner />
        </div>
      ) : shouldShowStoreLocatorForm ? (
        <div className="store-locator-sidebar">
          <StoreLocatorForm
            storeLocatorType={storeLocatorType}
            selectedVariant={selectedVariant}
            page={page}
            analyticsEventDetails={bopisEventDetailsTemplate}
          />
        </div>
      ) : (
        <div className="store-locator-sidebar">
          {storeLocatorType === 'sdd' && (
            <div className="store-locator-sidebar-tabs">
              <Tabs className="store-locator-tabs" value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab
                  className={`store-locator-tab-button ${activeTab === 'shipping' ? 'active' : ''}`}
                  label="Shipping"
                  value="shipping"
                />
                <Tab
                  className={`store-locator-tab-button ${activeTab === 'returns' ? 'active' : ''}`}
                  label="Returns"
                  value="returns"
                />
              </Tabs>
            </div>
          )}
          {activeTab === 'shipping' ? (
            <>
              {product && (
                <div className="store-locator-sidebar-product">
                  {product.images && (
                    <div className="store-locator-product-image">
                      <img src={product?.images[0] ? product?.images[0] : product?.images[1]} alt={product.title}></img>
                    </div>
                  )}
                  <div className="store-locator-sidebar-product-text">
                    {product.baseTitle && <h4 className="store-locator-product-title">{product.baseTitle}</h4>}
                    <div className="store-locator-product-variants">
                      {product.colorName && (
                        <p>
                          <span>Color:</span> {product.colorName}
                        </p>
                      )}
                      {userSelectedSize && (
                        <p>
                          <span>Size:</span> {userSelectedSize}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="store-locator-sidebar-form">
                <h5 className="h5">
                  {storeLocatorType !== 'sdd' ? 'Check Nearby Store Inventory' : 'Check shipping options'}
                </h5>
                <StoreLocatorForm
                  storeLocatorType={storeLocatorType}
                  selectedVariant={selectedVariant}
                  page={page}
                  analyticsEventDetails={bopisEventDetailsTemplate}
                />
                {page === 'cart' ? (
                  <div className="cart-availability">
                    <p>Availability for all items in your bag</p>
                    <span className="icon-bag"></span>
                  </div>
                ) : null}
                {shouldShowStoresData ? (
                  <>
                    <div className="store-locator-sidebar-results">
                      <p className="store-locator-sidebar-results__store-count">
                        {pluralize(storesData.length, `store`)} found near {postalCodeToDisplay}
                      </p>
                      {pricingDisclaimer && storeLocatorType === 'fis' ? (
                        <p className="store-locator-pricing-disclaimer black-text">{pricingDisclaimer}</p>
                      ) : null}
                    </div>
                    <div className="store-locator-tile-sidebar-container">
                      {storesData.map((storeAvailabilityData: IStoreAvailabilityData) => (
                        <StoreLocatorTile
                          key={storeAvailabilityData.location_id}
                          store={storeAvailabilityData}
                          page={page}
                          // cart will always show original copy
                          isAvailableOnline={page === 'cart' ? true : Boolean(isAvailableOnline)}
                          showFullTile={true}
                          inputName="sidebar"
                          storeSelectionEnabled={storeSelectionEnabled}
                          storeLocatorType={storeLocatorType}
                        />
                      ))}
                    </div>
                  </>
                ) : shouldShowTileMessage && storeLocatorType !== 'sdd' ? (
                  <p className="store-locator-no-results">{hasError ? errorMessage : noResultsCopy}</p>
                ) : null}
                {storeLocatorType === 'sdd' && (
                  <div className="store-locator-sidebar-sdd-container">
                    {<SddContainer shippingInfoFromOMS={shippingInfo} hasLuxuryTag={hasLuxuryTag} />}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="store-locator-returns-tab">
              <h5 className="h5">Return Policy</h5>
              <p className="returns-info">{SiteSettings?.sddReturnsInfo}</p>
            </div>
          )}
        </div>
      )}
    </AloSidebar>
  );
};

StoreLocatorSidebar = observer(StoreLocatorSidebar);

export { StoreLocatorSidebar };

const isError = (storeLocatorType: string = '', page: string = '') => {
  switch (storeLocatorType) {
    case 'fis':
      return Boolean(findInStoreState.storesDataError);
    case 'sdd':
      return page === 'pdp' ? Boolean(sddState.pdpSddDataError) : Boolean(sddState.cartSddDataError);
    case 'bopis':
      return page === 'pdp' ? Boolean(bopisState.pdpStoresDataError) : Boolean(bopisState.cartStoresDataError);
    default:
      return false;
  }
};
